import React, { useState } from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { FileText, File as FileIcon, Receipt, Loader2, Sparkles, Eye, Download, Mail, Lock } from 'lucide-react';

// Simple toggle component for consistency with mobile
function SimpleToggle({ checked, onChange, color = 'purple', disabled = false }) {
  const colorClasses = {
    purple: 'bg-purple-500',
    blue: 'bg-blue-500',
    indigo: 'bg-indigo-500',
    red: 'bg-red-500',
  };
  
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${checked ? colorClasses[color] : 'bg-gray-300'}`}
    >
      <span
        className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`}
      />
    </button>
  );
}

// Default values that should be treated as "empty" for border styling
const DEFAULT_VALUES = {
  invoice_count: 1
};

// Helper function to check if a dropdown has a value selected (for black border)
const hasSelectedValue = (field, value) => {
  // For invoice_count, any valid selection (1 or 2) should show black border
  if (field === 'invoice_count') {
    return value === 1 || value === 2 || value === '1' || value === '2';
  }
  if (!value && value !== 0) return false;
  return true;
};

export function InvoiceContent({
  formData, handleFieldChange, preventWheelChange, editingLoad,
  // Single invoice
  invoiceFile, setInvoiceFile, permitFile, setPermitFile, otherBills, setOtherBills,
  removePermitFile, setRemovePermitFile,
  generating, handleGenerateInvoiceClick, handleSendEmailClick,
  // Multi invoice (BALAJI)
  invoiceFile1, setInvoiceFile1, invoiceFile2, setInvoiceFile2,
  permitFile1, setPermitFile1, permitFile2, setPermitFile2,
  otherBills1, setOtherBills1, otherBills2, setOtherBills2,
  generating1, generating2, handleGenerateInvoice1Click, handleGenerateInvoice2Click,
  invoiceFile1Ref, invoiceFile2Ref, permitFile1Ref, permitFile2Ref,
  compact = false,
  locked = false,
  // Manual invoice validation
  manualInvoiceErrors = {}, clearManualInvoiceError,
  // Peek next invoice number
  peekedNumber, peeking, peekNextInvoiceNumber,
}) {
  const isBalaji = formData.client_name === 'BALAJI CASTINGS PVT LTD';
  const isSVE = formData.client_name === 'SVE CASTINGS PVT LTD';
  const isMultiInvoiceClient = isBalaji || isSVE;
  const isTwoInvoice = isMultiInvoiceClient && Number(formData.invoice_count) === 2;
  const isSingleInvoice = !isMultiInvoiceClient || Number(formData.invoice_count) === 1;
  const lockedStyle = 'bg-slate-100 cursor-not-allowed opacity-70';

  // Manual invoice-number override is available to super users only
  const canUseManualInvoice = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'super_approver'; }
    catch { return false; }
  }, []);

  // Locked view - show read-only data
  if (locked) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
          <Lock className="w-4 h-4" />
          <span>This section is locked</span>
        </div>
        
        {isMultiInvoiceClient && (
          <div className="space-y-1 max-w-[180px]">
            <Label className="text-xs text-slate-500 ml-1">No. of Invoices</Label>
            <Input value={formData.invoice_count || '1'} readOnly className={`h-10 rounded-xl ${lockedStyle}`} />
          </div>
        )}
        
        <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Product</Label>
            <Input value={formData.product} readOnly placeholder="Product" className={`h-10 rounded-xl ${lockedStyle}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Rate (per kg)</Label>
            <Input value={formData.rate} readOnly placeholder="Rate (per kg)" className={`h-10 rounded-xl max-w-[180px] ${lockedStyle}`} />
          </div>
        </div>
        
        {isTwoInvoice ? (
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-xl border">
              <h4 className="font-semibold text-sm mb-2">Invoice 1</h4>
              <div className="grid grid-cols-2 gap-2">
                <Input value={formData.kgs_1} readOnly placeholder="Kgs" className={`h-10 rounded-xl ${lockedStyle}`} />
                <Input value={formData.tons_1 || ''} readOnly placeholder="Tons" className={`h-10 rounded-xl ${lockedStyle}`} />
              </div>
              <p className="text-sm text-slate-600 mt-2">Amount: ₹{formData.invoice_amount_1 || 0}</p>
              {formData.generated_invoice_number_1 && <p className="text-xs text-slate-500">Invoice: {formData.generated_invoice_number_1}</p>}
            </div>
            <div className="p-3 bg-slate-50 rounded-xl border">
              <h4 className="font-semibold text-sm mb-2">Invoice 2</h4>
              <div className="grid grid-cols-2 gap-2">
                <Input value={formData.kgs_2} readOnly placeholder="Kgs" className={`h-10 rounded-xl ${lockedStyle}`} />
                <Input value={formData.tons_2 || ''} readOnly placeholder="Tons" className={`h-10 rounded-xl ${lockedStyle}`} />
              </div>
              <p className="text-sm text-slate-600 mt-2">Amount: ₹{formData.invoice_amount_2 || 0}</p>
              {formData.generated_invoice_number_2 && <p className="text-xs text-slate-500">Invoice: {formData.generated_invoice_number_2}</p>}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className={`grid ${compact ? 'grid-cols-2 gap-2' : 'grid-cols-3 gap-3'}`}>
              <Input value={formData.kgs} readOnly placeholder="Kgs" className={`h-10 rounded-xl ${lockedStyle}`} />
              <Input value={formData.tons || ''} readOnly placeholder="Tons" className={`h-10 rounded-xl ${lockedStyle}`} />
              {!compact && <Input value={formData.invoice_amount ? `₹${formData.invoice_amount}` : ''} readOnly placeholder="Amount" className={`h-10 rounded-xl ${lockedStyle}`} />}
            </div>
            {formData.generated_invoice_number && <p className="text-xs text-slate-500">Invoice: {formData.generated_invoice_number}</p>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Invoice Count selector for BALAJI and SVE */}
      {isMultiInvoiceClient && (
        <div className="space-y-1 max-w-[180px]">
          <Label className="text-xs text-slate-500 ml-1">No. of Invoices *</Label>
          <Select value={String(formData.invoice_count || '')} onValueChange={(v) => handleFieldChange('invoice_count', parseInt(v))}>
            <SelectTrigger data-testid="invoice-count-select" className={`h-10 rounded-xl ${hasSelectedValue('invoice_count', formData.invoice_count) ? 'border-slate-900' : ''}`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1</SelectItem>
              <SelectItem value="2">2</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Show only after invoice count is selected for multi-invoice clients, or always for other clients */}
      {(!isMultiInvoiceClient || formData.invoice_count) && (
        <>
          {/* Shared Product & Rate */}
          <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 ml-1">Product *</Label>
              <Input data-testid="product-input" value={formData.product} onChange={(e) => handleFieldChange('product', e.target.value)} placeholder="Product *" className={`h-10 rounded-xl ${formData.product ? 'border-slate-900' : ''}`} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 ml-1">Rate (per kg) *</Label>
              <Input data-testid="rate-input" type="number" value={formData.rate} onChange={(e) => handleFieldChange('rate', e.target.value)} onWheel={preventWheelChange} placeholder="Rate (per kg) *" className={`h-10 rounded-xl max-w-[180px] ${formData.rate ? 'border-slate-900' : ''}`} />
            </div>
          </div>

          {/* SINGLE INVOICE MODE */}
          {isSingleInvoice && (
            <SingleInvoiceMode
              formData={formData} handleFieldChange={handleFieldChange} preventWheelChange={preventWheelChange}
              editingLoad={editingLoad}
              invoiceFile={invoiceFile} setInvoiceFile={setInvoiceFile}
              permitFile={permitFile} setPermitFile={setPermitFile}
              otherBills={otherBills} setOtherBills={setOtherBills}
              removePermitFile={removePermitFile} setRemovePermitFile={setRemovePermitFile}
              generating={generating} handleGenerateInvoiceClick={handleGenerateInvoiceClick}
              handleSendEmailClick={handleSendEmailClick}
              compact={compact}
              manualInvoiceErrors={manualInvoiceErrors} clearManualInvoiceError={clearManualInvoiceError}
              peekedNumber={peekedNumber} peeking={peeking} peekNextInvoiceNumber={peekNextInvoiceNumber}
            />
          )}

          {/* DUAL INVOICE MODE */}
          {isTwoInvoice && (
            <DualInvoiceMode
              formData={formData} handleFieldChange={handleFieldChange} preventWheelChange={preventWheelChange}
              invoiceFile1={invoiceFile1} setInvoiceFile1={setInvoiceFile1}
              invoiceFile2={invoiceFile2} setInvoiceFile2={setInvoiceFile2}
              permitFile1={permitFile1} setPermitFile1={setPermitFile1}
              permitFile2={permitFile2} setPermitFile2={setPermitFile2}
              otherBills1={otherBills1} setOtherBills1={setOtherBills1}
              otherBills2={otherBills2} setOtherBills2={setOtherBills2}
              generating1={generating1} generating2={generating2}
              handleGenerateInvoice1Click={handleGenerateInvoice1Click}
              handleGenerateInvoice2Click={handleGenerateInvoice2Click}
              invoiceFile1Ref={invoiceFile1Ref} invoiceFile2Ref={invoiceFile2Ref}
              permitFile1Ref={permitFile1Ref} permitFile2Ref={permitFile2Ref}
              handleSendEmailClick={handleSendEmailClick}
              compact={compact}
              manualInvoiceErrors={manualInvoiceErrors} clearManualInvoiceError={clearManualInvoiceError}
              peekedNumber={peekedNumber} peeking={peeking} peekNextInvoiceNumber={peekNextInvoiceNumber}
            />
          )}
        </>
      )}
    </div>
  );
}

function SingleInvoiceMode({
  formData, handleFieldChange, preventWheelChange, editingLoad,
  invoiceFile, setInvoiceFile, permitFile, setPermitFile, otherBills, setOtherBills,
  removePermitFile, setRemovePermitFile,
  generating, handleGenerateInvoiceClick, handleSendEmailClick, compact,
  manualInvoiceErrors = {}, clearManualInvoiceError,
  peekedNumber, peeking, peekNextInvoiceNumber,
}) {
  const inputH = 'h-10 rounded-xl';
  const [generateEnabled, setGenerateEnabled] = useState(true);
  const canUseManualInvoice = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'super_approver'; }
    catch { return false; }
  }, []);

  return (
    <>
      {/* Row 1: Kgs, Tons, Amount */}
      <div className={`grid ${compact ? 'grid-cols-2 gap-2' : 'grid-cols-3 gap-3'}`}>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500 ml-1">Kgs *</Label>
          <Input data-testid="kgs-input" type="number" value={formData.kgs} onChange={(e) => handleFieldChange('kgs', e.target.value)} onWheel={preventWheelChange} placeholder="Kgs *" className={`${inputH} ${formData.kgs ? 'border-slate-900' : ''}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500 ml-1">Tons</Label>
          <Input type="number" value={formData.tons || ''} onChange={(e) => handleFieldChange('tons', e.target.value)} onWheel={preventWheelChange} placeholder="Tons" className={`${inputH} ${formData.tons ? 'border-slate-900' : ''}`} />
        </div>
        {!compact && (
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Amount</Label>
            <Input value={formData.invoice_amount ? `₹${formData.invoice_amount}` : ''} readOnly placeholder="Amount" className={`${inputH} bg-slate-50`} />
          </div>
        )}
      </div>
      {compact && formData.invoice_amount && (
        <div className="p-2 bg-slate-50 rounded-lg"><p className="text-xs text-slate-600">Amount: <strong>₹{formData.invoice_amount}</strong></p></div>
      )}
      {/* Row 2: Toggle + Manual Invoice No. + Peek + Gen button */}
      <div className={`flex ${compact ? 'flex-col gap-2' : 'items-end gap-3'}`}>
        <div className="flex items-center gap-2 shrink-0 pb-1">
          <SimpleToggle checked={generateEnabled} onChange={setGenerateEnabled} color="purple" />
          <span className="text-xs text-slate-600 whitespace-nowrap">Auto Gen</span>
        </div>
        <div className={`space-y-1 max-w-[220px] ${canUseManualInvoice ? '' : 'hidden'}`} data-testid="manual-invoice-wrapper">
          <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Manual Invoice No. (optional)</Label>
          <Input
            value={formData.manual_invoice_number ?? ''}
            onChange={(e) => {
              handleFieldChange('manual_invoice_number', e.target.value);
              if (clearManualInvoiceError) clearManualInvoiceError('manual_invoice_number');
            }}
            placeholder="e.g. MJ/SS25099"
            className={`${inputH} text-xs ${manualInvoiceErrors.manual_invoice_number ? 'border-red-500' : formData.manual_invoice_number ? 'border-slate-900' : ''}`}
            data-testid="manual-invoice-number"
          />
          {manualInvoiceErrors.manual_invoice_number && (
            <p className="text-[10px] text-red-600 leading-tight" data-testid="manual-invoice-error">{manualInvoiceErrors.manual_invoice_number}</p>
          )}
        </div>
        {!formData.generated_invoice_number && (
          <Button type="button" onClick={peekNextInvoiceNumber} disabled={peeking} className="h-10 rounded-xl px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 text-xs font-medium shrink-0" data-testid="peek-next-number-btn">
            {peeking ? '...' : peekedNumber ? `Next: ${peekedNumber}` : 'Next #?'}
          </Button>
        )}
        <Button data-testid="gen-invoice-btn" type="button" onClick={handleGenerateInvoiceClick} disabled={!generateEnabled || !formData.product || !formData.kgs || !formData.rate || generating} className={`h-10 rounded-xl ${compact ? 'w-full' : 'px-6'} font-semibold shrink-0 ${formData.generated_invoice_file ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'} text-white disabled:opacity-50`}>
          {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
          {formData.generated_invoice_file ? 'Regen INV' : 'Gen INV'}
        </Button>
      </div>
      {/* Generated invoice card */}
      {formData.generated_invoice_number && (
        <>
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-200">
            <div className="flex-1 min-w-0">
              <p className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-green-800 truncate`}>{formData.generated_invoice_number}</p>
              <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-green-600`}>Amount: ₹{formData.generated_invoice_amount}</p>
            </div>
            <a href={formData.generated_invoice_file} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center bg-blue-500 text-white rounded-lg" title="View PDF"><Eye className="w-4 h-4" /></a>
            <a href={formData.generated_invoice_file} download={`${formData.generated_invoice_number?.replace('PREVIEW-', '') || 'invoice'}.pdf`} className="w-8 h-8 flex items-center justify-center bg-emerald-500 text-white rounded-lg" title="Download"><Download className="w-4 h-4" /></a>
            <button type="button" onClick={() => handleSendEmailClick(formData.generated_invoice_number, formData.generated_invoice_amount)} className="w-8 h-8 flex items-center justify-center bg-purple-500 text-white rounded-lg" title="Send Email"><Mail className="w-4 h-4" /></button>
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={() => handleFieldChange('_clear_generated_invoice', true)} className="text-xs text-red-500 hover:text-red-700 underline">Remove Generated Invoice</button>
          </div>
        </>
      )}
      {/* File uploads */}
      <div className={`grid ${compact ? 'grid-cols-2 gap-2' : 'grid-cols-3 gap-2'}`}>
        <div className="space-y-1">
          <label className={`flex flex-col items-center justify-center h-14 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${invoiceFile || formData.generated_invoice_file ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-300 hover:border-red-400'}`}>
            <input data-testid="invoice-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setInvoiceFile(e.target.files[0])} className="hidden" />
            <FileText className={`w-5 h-5 ${invoiceFile || formData.generated_invoice_file ? 'text-emerald-600' : 'text-red-500'}`} />
            <span className={`text-[10px] mt-0.5 font-medium ${invoiceFile || formData.generated_invoice_file ? 'text-emerald-600' : 'text-red-500'}`}>Invoice *</span>
          </label>
          {invoiceFile && (
            <div className="flex items-center justify-between bg-emerald-50 rounded px-1.5 py-0.5">
              <p className="text-[9px] text-emerald-600 truncate flex-1">{invoiceFile.name}</p>
              <button type="button" onClick={() => setInvoiceFile(null)} className="text-[9px] text-red-500 ml-1">✕</button>
            </div>
          )}
        </div>
        <div className="space-y-1">
          <label className={`flex flex-col items-center justify-center h-14 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${permitFile || (!removePermitFile && editingLoad?.permit_filename) ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-300 hover:border-red-400'}`}>
            <input data-testid="permit-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setPermitFile(e.target.files[0])} className="hidden" />
            <FileIcon className={`w-5 h-5 ${permitFile || (!removePermitFile && editingLoad?.permit_filename) ? 'text-emerald-600' : 'text-red-500'}`} />
            <span className={`text-[10px] mt-0.5 font-medium ${permitFile || (!removePermitFile && editingLoad?.permit_filename) ? 'text-emerald-600' : 'text-red-500'}`}>Permit *</span>
          </label>
          {permitFile && (
            <div className="flex items-center justify-between bg-emerald-50 rounded px-1.5 py-0.5">
              <p className="text-[9px] text-emerald-600 truncate flex-1">{permitFile.name}</p>
              <button type="button" onClick={() => setPermitFile(null)} className="text-[9px] text-red-500 ml-1">✕</button>
            </div>
          )}
          {!permitFile && editingLoad?.permit_filename && !removePermitFile && (
            <div className="flex items-center justify-between bg-blue-50 rounded px-1.5 py-0.5">
              <p className="text-[9px] text-slate-600 truncate flex-1">{editingLoad.permit_filename}</p>
              <button type="button" onClick={() => setRemovePermitFile(true)} className="text-[9px] text-red-500 ml-1">✕</button>
            </div>
          )}
          {removePermitFile && editingLoad?.permit_filename && (
            <div className="flex items-center justify-between bg-red-50 rounded-lg px-2 py-1">
              <p className="text-[10px] text-red-600">Will remove</p>
              <button type="button" onClick={() => setRemovePermitFile(false)} className="text-[10px] text-blue-500 ml-1">Undo</button>
            </div>
          )}
        </div>
        <div className="space-y-1">
          <label className={`flex flex-col items-center justify-center ${compact ? 'h-16' : 'h-20'} rounded-xl border-2 border-dashed cursor-pointer transition-colors ${otherBills.length > 0 ? 'bg-emerald-50 border-emerald-400' : 'bg-slate-50 border-slate-300 hover:border-slate-400'}`}>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={(e) => setOtherBills(Array.from(e.target.files || []))} className="hidden" />
            <Receipt className={`w-6 h-6 ${otherBills.length > 0 ? 'text-emerald-600' : 'text-slate-400'}`} />
            <span className={`text-xs mt-1 font-medium ${otherBills.length > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>Other Bills</span>
          </label>
          {otherBills.length > 0 && (
            <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-2 py-1">
              <p className="text-[10px] text-emerald-600 truncate flex-1">{otherBills.length} file(s)</p>
              <button type="button" onClick={() => setOtherBills([])} className="text-[10px] text-red-500 ml-1">✕</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function DualInvoiceMode({
  formData, handleFieldChange, preventWheelChange,
  invoiceFile1, setInvoiceFile1, invoiceFile2, setInvoiceFile2,
  permitFile1, setPermitFile1, permitFile2, setPermitFile2,
  otherBills1, setOtherBills1, otherBills2, setOtherBills2,
  generating1, generating2, handleGenerateInvoice1Click, handleGenerateInvoice2Click,
  invoiceFile1Ref, invoiceFile2Ref, permitFile1Ref, permitFile2Ref,
  handleSendEmailClick, compact,
  manualInvoiceErrors = {}, clearManualInvoiceError,
  peekedNumber, peeking, peekNextInvoiceNumber,
}) {
  return (
    <>
      <div className="p-2 bg-indigo-100 rounded-lg border border-indigo-300">
        <p className="text-xs font-semibold text-indigo-700">
          Total: {formData.kgs || '0'} Kgs | {formData.tons || '0'} Tons | ₹{formData.invoice_amount || '0'}
        </p>
      </div>
      {/* Invoice 1 */}
      <InvoiceCard
        num={1} color="blue" formData={formData} handleFieldChange={handleFieldChange} preventWheelChange={preventWheelChange}
        kgsField="kgs_1" tonsField="tons_1" amountField="invoice_amount_1"
        genFileField="generated_invoice_file_1" genNumField="generated_invoice_number_1" genAmtField="generated_invoice_amount_1"
        invoiceFile={invoiceFile1} setInvoiceFile={setInvoiceFile1}
        permitFile={permitFile1} setPermitFile={setPermitFile1}
        otherBills={otherBills1} setOtherBills={setOtherBills1}
        generating={generating1} handleGenerateClick={handleGenerateInvoice1Click}
        invoiceFileRef={invoiceFile1Ref} permitFileRef={permitFile1Ref}
        handleSendEmailClick={handleSendEmailClick}
        compact={compact}
        manualInvoiceErrors={manualInvoiceErrors} clearManualInvoiceError={clearManualInvoiceError}
        peekedNumber={peekedNumber} peeking={peeking} peekNextInvoiceNumber={peekNextInvoiceNumber}
      />
      {/* Invoice 2 */}
      <InvoiceCard
        num={2} color="purple" formData={formData} handleFieldChange={handleFieldChange} preventWheelChange={preventWheelChange}
        kgsField="kgs_2" tonsField="tons_2" amountField="invoice_amount_2"
        genFileField="generated_invoice_file_2" genNumField="generated_invoice_number_2" genAmtField="generated_invoice_amount_2"
        invoiceFile={invoiceFile2} setInvoiceFile={setInvoiceFile2}
        permitFile={permitFile2} setPermitFile={setPermitFile2}
        otherBills={otherBills2} setOtherBills={setOtherBills2}
        generating={generating2} handleGenerateClick={handleGenerateInvoice2Click}
        disabled={!formData.kgs_1 || !(invoiceFile1 || formData.generated_invoice_file_1 || formData.invoice_file_url_1) || !permitFile1}
        invoiceFileRef={invoiceFile2Ref} permitFileRef={permitFile2Ref}
        handleSendEmailClick={handleSendEmailClick}
        compact={compact}
        manualInvoiceErrors={manualInvoiceErrors} clearManualInvoiceError={clearManualInvoiceError}
      />
    </>
  );
}

function InvoiceCard({
  num, color, formData, handleFieldChange, preventWheelChange,
  kgsField, tonsField, amountField, genFileField, genNumField, genAmtField,
  invoiceFile, setInvoiceFile, permitFile, setPermitFile, otherBills, setOtherBills,
  generating, handleGenerateClick, disabled,
  invoiceFileRef, permitFileRef, handleSendEmailClick, compact,
  manualInvoiceErrors = {}, clearManualInvoiceError,
  peekedNumber, peeking, peekNextInvoiceNumber,
}) {
  const bgColor = color === 'blue' ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200';
  const titleColor = color === 'blue' ? 'text-blue-800' : 'text-purple-800';
  const badgeBg = color === 'blue' ? 'bg-blue-600' : 'bg-purple-600';
  const [generateEnabled, setGenerateEnabled] = useState(true);
  const canUseManualInvoice = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'super_approver'; }
    catch { return false; }
  }, []);

  return (
    <div className={`p-4 rounded-xl border ${bgColor} ${disabled ? 'opacity-60' : ''}`}>
      <div className={`text-sm font-bold ${titleColor} mb-3 flex items-center gap-2`}>
        <span className={`w-6 h-6 rounded-full ${badgeBg} text-white flex items-center justify-center text-xs`}>{num}</span>
        Invoice {num}
        {formData[genFileField] && <span className="ml-auto text-emerald-600 text-xs">&#10003;</span>}
      </div>
      {disabled && <p className="text-xs text-amber-600 mb-2">⚠️ Complete Invoice 1 details first (Kgs, Invoice File, Permit File)</p>}
      <div className={`space-y-3 ${disabled ? 'pointer-events-none' : ''}`}>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1"><Label className="text-xs">Kgs *</Label><Input data-testid={`kgs-input-${num}`} type="number" value={formData[kgsField]} onChange={(e) => handleFieldChange(kgsField, e.target.value)} onWheel={preventWheelChange} placeholder="Kgs" className={`h-10 rounded-lg ${formData[kgsField] ? 'border-slate-900' : ''}`} disabled={disabled} /></div>
          <div className="space-y-1"><Label className="text-xs">Tons</Label><Input data-testid={`tons-input-${num}`} type="number" value={formData[tonsField] || ''} onChange={(e) => handleFieldChange(tonsField, e.target.value)} onWheel={preventWheelChange} placeholder="Tons" className={`h-10 rounded-lg ${formData[tonsField] ? 'border-slate-900' : ''}`} disabled={disabled} /></div>
          <div className="space-y-1"><Label className="text-xs">Amount</Label><Input value={formData[amountField] ? `₹${formData[amountField]}` : ''} readOnly className="h-10 rounded-lg bg-slate-50" /></div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <SimpleToggle checked={generateEnabled} onChange={setGenerateEnabled} color={color === 'blue' ? 'blue' : 'purple'} disabled={disabled} />
            <span className="text-xs text-slate-600">Enable Generation</span>
          </div>
          <div className={`flex items-center gap-2 ${canUseManualInvoice ? '' : 'hidden'}`}>
            <Input
              value={formData[`manual_invoice_number_${num}`] ?? ''}
              onChange={(e) => {
                handleFieldChange(`manual_invoice_number_${num}`, e.target.value);
                if (clearManualInvoiceError) clearManualInvoiceError(`manual_invoice_number_${num}`);
              }}
              placeholder={`Invoice No. ${num} (optional, e.g. MJ/SS25001)`}
              className={`h-10 rounded-lg text-xs flex-1 ${manualInvoiceErrors[`manual_invoice_number_${num}`] ? 'border-red-500' : formData[`manual_invoice_number_${num}`] ? 'border-slate-900' : ''}`}
              disabled={disabled}
              data-testid={`manual-invoice-number-${num}`}
            />
          </div>
          {!formData[genNumField] && peekNextInvoiceNumber && (
            <Button type="button" onClick={peekNextInvoiceNumber} disabled={peeking || disabled} className="h-10 rounded-lg px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 text-xs font-medium shrink-0" data-testid={`peek-next-number-btn-inv${num}`}>
              {peeking ? '...' : peekedNumber ? `Next: ${peekedNumber}` : 'Peek Next #'}
            </Button>
          )}
          <div className={canUseManualInvoice ? '' : 'hidden'}>
          {manualInvoiceErrors[`manual_invoice_number_${num}`] && (
            <p className="text-[10px] text-red-600 leading-tight" data-testid={`manual-invoice-error-${num}`}>{manualInvoiceErrors[`manual_invoice_number_${num}`]}</p>
          )}
          </div>
          <div className="flex gap-1.5">
            <Button data-testid={`gen-invoice-btn-${num}`} type="button" onClick={handleGenerateClick} disabled={!generateEnabled || !formData.product || !formData[kgsField] || !formData.rate || generating || disabled} className={`w-full h-10 rounded-lg font-semibold ${formData[genFileField] ? 'bg-green-500 hover:bg-green-600' : 'bg-red-400 hover:bg-red-500'} text-white disabled:opacity-50`}>
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {formData[genFileField] ? `Regen INV${num}` : `Gen INV${num}`}
            </Button>
          </div>
        </div>
        {formData[genNumField] && (
          <>
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-green-800 truncate">{formData[genNumField]}</p>
                <p className="text-[10px] text-green-600">₹{formData[genAmtField]}</p>
              </div>
              <a href={formData[genFileField]} target="_blank" rel="noopener noreferrer" className="w-7 h-7 flex items-center justify-center bg-blue-500 text-white rounded" title="View"><Eye className="w-3 h-3" /></a>
              <a href={formData[genFileField]} download={`${(formData[genNumField] || 'invoice').replace('PREVIEW-', '')}.pdf`} className="w-7 h-7 flex items-center justify-center bg-emerald-500 text-white rounded" title="Download"><Download className="w-3 h-3" /></a>
              <button type="button" onClick={() => handleSendEmailClick(formData[genNumField], formData[genAmtField], 'regular')} className="w-7 h-7 flex items-center justify-center bg-purple-500 text-white rounded" title="Send Email"><Mail className="w-3 h-3" /></button>
            </div>
            <div className="flex justify-end"><button type="button" onClick={() => handleFieldChange(`_clear_gen_inv_${num}`, true)} className="text-[10px] text-red-500 hover:text-red-700 underline">Remove Generated Invoice</button></div>
          </>
        )}
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1">
            <label className={`flex flex-col items-center justify-center h-16 rounded-lg border-2 border-dashed cursor-pointer ${invoiceFile || formData[genFileField] ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-300'}`}>
              <input data-testid={`invoice-file-input-${num}`} ref={invoiceFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)} className="hidden" disabled={disabled} />
              <FileText className={`w-4 h-4 ${invoiceFile || formData[genFileField] ? 'text-emerald-600' : 'text-red-500'}`} />
              <span className={`text-[9px] mt-0.5 ${invoiceFile || formData[genFileField] ? 'text-emerald-600' : 'text-red-500'}`}>Invoice *</span>
            </label>
            {invoiceFile && <div className="flex items-center justify-between bg-emerald-50 rounded px-1 py-0.5"><p className="text-[9px] text-emerald-600 truncate flex-1">{invoiceFile.name}</p><button type="button" onClick={() => setInvoiceFile(null)} className="text-[9px] text-red-500 ml-1">✕</button></div>}
          </div>
          <div className="space-y-1">
            <label className={`flex flex-col items-center justify-center h-16 rounded-lg border-2 border-dashed cursor-pointer ${permitFile ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-300'}`}>
              <input data-testid={`permit-file-input-${num}`} ref={permitFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setPermitFile(e.target.files?.[0] || null)} className="hidden" disabled={disabled} />
              <FileIcon className={`w-4 h-4 ${permitFile ? 'text-emerald-600' : 'text-red-500'}`} />
              <span className={`text-[9px] mt-0.5 ${permitFile ? 'text-emerald-600' : 'text-red-500'}`}>Permit *</span>
            </label>
            {permitFile && <div className="flex items-center justify-between bg-emerald-50 rounded px-1 py-0.5"><p className="text-[9px] text-emerald-600 truncate flex-1">{permitFile.name}</p><button type="button" onClick={() => setPermitFile(null)} className="text-[9px] text-red-500 ml-1">✕</button></div>}
          </div>
          <div className="space-y-1">
            <label className={`flex flex-col items-center justify-center h-16 rounded-lg border-2 border-dashed cursor-pointer ${otherBills.length > 0 ? 'bg-emerald-50 border-emerald-400' : 'bg-slate-50 border-slate-300'}`}>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={(e) => setOtherBills(Array.from(e.target.files || []))} className="hidden" disabled={disabled} />
              <Receipt className={`w-4 h-4 ${otherBills.length > 0 ? 'text-emerald-600' : 'text-slate-400'}`} />
              <span className={`text-[9px] mt-0.5 ${otherBills.length > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>Other</span>
            </label>
            {otherBills.length > 0 && <div className="flex items-center justify-between bg-emerald-50 rounded px-1 py-0.5"><p className="text-[9px] text-emerald-600">{otherBills.length} file(s)</p><button type="button" onClick={() => setOtherBills([])} className="text-[9px] text-red-500 ml-1">✕</button></div>}
          </div>
        </div>
      </div>
    </div>
  );
}
